import { autoUpdate, flip, hide, offset, shift, useFloating } from '@floating-ui/vue'
import { posToDOMRect } from '@tiptap/core'
import { computed, nextTick, onUnmounted, ref, unref, watch } from 'vue'

/**
 * @typedef {object} Options
 * @property {Array} datas - Array of suggestion data items. Each item should have id, label, and value.
 * @property {() => void} onOpen - Callback function when the menu opens.
 * @property {() => void} onClose - Callback function when the menu closes.
 * @property {(query: string) => void} onSearch - Callback function when the search query changes.
 * @property {(event: KeyboardEvent) => void} onEnter - Callback function for Enter key press.
 * @property {(response: any) => void} onSelect - Callback function for select variable.
 */

/**
 * Composable for managing a floating variable suggestion menu triggered by the '{{' characters.
 *
 * @param {import('vue').Ref<import('@tiptap/vue-2').Editor>} editorRef - Ref containing the Tiptap editor instance.
 * @param {Options} [options] - Configuration options.
 */
export function useTiptapVariableMention(editorRef, options = {}) {
  const {
    datas,
    onOpen,
    onClose,
    onSearch,
    onSelect,
  } = options
  const editor = editorRef

  const isOpen = ref(false)
  const floatingRef = ref(null)
  const activeTriggerPos = ref(null) // Store the start position of the '{{' trigger
  const searchQuery = ref('')

  const position = ref('')
  const suggestionContainerRef = ref(null)

  const suggestions = computed(() => {
    const suggestionLists = unref(datas) || []

    if (searchQuery.value.length === 0) return suggestionLists

    const search = searchQuery.value.toLowerCase()
    const filteredBySearch = suggestionLists.filter(item =>
      item.label.toLowerCase().includes(search),
    )

    return filteredBySearch
  })

  // Virtual element for Floating UI based on the '{{' position
  const virtualRef = ref({
    getBoundingClientRect: () => ({
      width: 0,
      height: 0,
      x: 0,
      y: 0,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    }),
  })

  const { floatingStyles, update } = useFloating(virtualRef, floatingRef, {
    open: isOpen,
    placement: 'top-start', // Position menu below the trigger
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 8 }),
      shift({ padding: 5 }),
      hide({ strategy: 'referenceHidden' }),
    ],
  })

  const updateVirtualReference = (pos) => {
    if (!editor?.value?.view || pos === null) return

    // Use posToDOMRect to get the bounding box of the first '{' character of the trigger
    virtualRef.value = {
      getBoundingClientRect: () => posToDOMRect(editor.value.view, pos, pos + 1),
    }
    update()
  }

  const openMenu = (pos) => {
    if (isOpen.value) return
    activeTriggerPos.value = pos // pos is the start of '{{'
    searchQuery.value = ''
    updateVirtualReference(pos)
    isOpen.value = true

    nextTick(() => {
      position.value = suggestions.value[0]?.id || null
      adjustScrollPosition()
    })
  }

  const closeMenu = () => {
    if (!isOpen.value) return // Already closed
    isOpen.value = false
    activeTriggerPos.value = null
    searchQuery.value = ''
  }

  const checkAndHandleUpdate = () => {
    if (!editor.value || !editor.value.view || !editor.value.state) return

    const { state } = editor.value
    const { selection } = state
    const { from, to } = selection

    if (from !== to) {
      if (isOpen.value) closeMenu()
      return
    }

    const currentPos = from

    if (!isOpen.value) {
      // Check if '{{' was just typed
      if (currentPos >= 2) {
        const charsBeforeCursor = state.doc.textBetween(currentPos - 2, currentPos, '', '')
        if (charsBeforeCursor === '{{') {
          openMenu(currentPos - 2) // Pass the start position of '{{'
        }
      }
    }
    else {
      // Menu is open
      if (activeTriggerPos.value === null || currentPos <= activeTriggerPos.value + 1) {
        // Cursor moved before or into the '{{' trigger
        closeMenu()
        return
      }

      const triggerChars = state.doc.textBetween(activeTriggerPos.value, activeTriggerPos.value + 2, '', '')
      if (triggerChars !== '{{') {
        // The original '{{' characters were changed/deleted
        closeMenu()
        return
      }

      const newQuery = state.doc.textBetween(activeTriggerPos.value + 2, currentPos, '', '')

      if (newQuery.endsWith(' ') || newQuery.endsWith('}')) { // Closing with a space or a single '}'
        closeMenu()
        return
      }

      if (searchQuery.value !== newQuery) {
        searchQuery.value = newQuery
        onSearch?.(newQuery)
      }

      updateVirtualReference(activeTriggerPos.value)
    }
  }

  const handleKeyDown = (event) => {
    if (!isOpen.value || !editor.value?.isFocused) return

    const key = event.key
    let handled = false

    switch (key) {
      case 'ArrowUp':
        handled = true
        handleArrowUp()
        break
      case 'ArrowDown':
        handled = true
        handleArrowDown()
        break
      case 'Enter':
        handled = true
        handleSelect()
        break
      case 'Escape':
        closeMenu()
        handled = true
        break
    }

    if (handled) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  function handleSelect() {
    if (!editor.value || suggestions.value.length === 0 || activeTriggerPos.value === null) {
      return
    }

    const selected = suggestions.value.find(item => item.id === position.value)
    if (!selected) {
      return
    }

    const from = activeTriggerPos.value // Start of '{{'
    const to = activeTriggerPos.value + 2 + searchQuery.value.length // End of '{{' + query

    const { view } = editor.value
    const { state } = view
    const endPosition = state.doc.resolve(to) // Resolve the position *after* the trigger text

    // Check if the node immediately after the variable trigger text starts with a space
    const nodeAfter = endPosition.nodeAfter
    const spaceExistsAfter = nodeAfter?.isText && nodeAfter.text.startsWith(' ')

    // Ensure 'selected' has id, label, and value for variableMention
    const variableAttrs = {
      id: selected.id,
      label: selected.label,
      value: selected.value,
    }

    // Build the content to insert: variableMention node, and potentially a space
    const contentToInsert = [
      {
        type: 'variableMention',
        attrs: variableAttrs,
      },
    ]

    if (!spaceExistsAfter) {
      contentToInsert.push({
        type: 'text',
        text: ' ',
      })
    }

    editor.value
      .chain()
      .focus()
      .insertContentAt({ from, to }, contentToInsert)
      .run()

    onSelect?.(selected)
  }

  function handleArrowUp() {
    if (suggestions.value.length === 0) return
    const currentIndex = suggestions.value.findIndex(response => response.id === position.value)
    const newIndex = Math.max(0, currentIndex - 1)
    position.value = suggestions.value[newIndex].id

    nextTick(() => adjustScrollPosition())
  }

  function handleArrowDown() {
    if (suggestions.value.length === 0) return

    const currentIndex = suggestions.value.findIndex(response => response.id === position.value)
    const newIndex = Math.min(suggestions.value.length - 1, currentIndex + 1)
    position.value = suggestions.value[newIndex].id

    nextTick(() => adjustScrollPosition())
  }

  function adjustScrollPosition() {
    if (suggestions.value.length === 0) return

    const container = suggestionContainerRef.value
    const selectedElement = document.querySelector('[data-hover="true"]')

    if (container && selectedElement) {
      const containerRect = container.getBoundingClientRect()
      const elementRect = selectedElement.getBoundingClientRect()

      if (elementRect.top < containerRect.top) {
        selectedElement.scrollIntoView({ block: 'center' })
      }
      else if (elementRect.bottom > containerRect.bottom) {
        selectedElement.scrollIntoView({ block: 'center' })
      }
    }
  }

  function isHighlighted(id) {
    return id === position.value
  }

  function setHighlighted(id) {
    position.value = id
  }

  watch(
    () => searchQuery.value,
    () => {
      nextTick(() => {
        position.value = suggestions.value[0]?.id || null
        adjustScrollPosition()
      })
    },
  )

  watch(
    () => unref(datas),
    () => {
      nextTick(() => {
        position.value = suggestions.value[0]?.id || null
        adjustScrollPosition()
      })
    },
  )

  watch(isOpen, (newValue, oldValue) => {
    if (newValue && !oldValue) {
      onOpen?.()
    }
    else if (!newValue && oldValue) {
      onClose?.()
    }
  })

  // Watch for editor instance availability and state changes
  watch(editor, (newEditor, oldEditor) => {
    if (oldEditor) {
      oldEditor.off('transaction', checkAndHandleUpdate)
      oldEditor.view?.dom.removeEventListener('keydown', handleKeyDown, true) // Use capture phase
    }
    if (newEditor) {
      // Use 'transaction' as it fires after state is fully updated
      newEditor.on('transaction', checkAndHandleUpdate)
      // Add keydown listener directly to the editor's DOM element
      // Use capture phase to intercept keys before Tiptap handles them
      newEditor.view?.dom.addEventListener('keydown', handleKeyDown, true)
      // Initial check
      checkAndHandleUpdate()
    }
    else {
      // Editor destroyed or unavailable
      if (isOpen.value) closeMenu()
    }
  }, { immediate: true }) // Run immediately

  onUnmounted(() => {
    if (editor.value) {
      editor.value.off('transaction', checkAndHandleUpdate)
      editor.value.view?.dom.removeEventListener('keydown', handleKeyDown, true)
    }
  })

  return {
    isOpen,
    floatingRef,
    floatingStyles,
    searchQuery,
    suggestions,
    suggestionContainerRef,
    isHighlighted,
    setHighlighted,
    handleSelect,
  }
}
